import React, {
  useCallback,
  useMemo,
  useEffect,
  useState,
  useRef,
  ReactNode,
  forwardRef,
  useImperativeHandle,
} from 'react';
import cx from 'clsx';
import { QueryClientProvider } from 'react-query';
import { RcFile } from 'antd/es/upload';
import message from 'antd/es/message';
import {
  $getRoot,
  // $isRangeSelection, $getSelection,
  PASTE_COMMAND,
} from 'lexical';
import { AutoLinkNode } from '@lexical/link';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { AutoLinkPlugin } from '@lexical/react/LexicalAutoLinkPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
// import { HashtagPlugin } from '@lexical/react/LexicalHashtagPlugin';
import MentionsPlugin from './plugins/MentionsPlugin';
import HashtagPlugin from './plugins/HashtagPlugin';
import SlashPlugin from './plugins/SlashPlugin';
import { MentionNode } from './nodes/MentionNode';
import { HashtagNode } from './nodes/HashtagNode';
import { SlashNode } from './nodes/SlashNode';
import { useTranslation } from 'react-i18next';

import { IconRcCloseIcon } from '@/assets/editor';
import { Loading, Button } from '@/components';
import { devLog, debounce, queryClient, useSyncI18n } from '@/utils';
import { useSwitch, useTheme, THEMES, useCommentEligibility, useChannelPick } from '@/hooks';
import { SUPPORTED_LOCALE_TYPE } from '@/locales';

import { PostFooter } from '../PostFooter';
import { useCommonImageUpload, clearUrlCardPreviewQuery, useUrlCardPreview } from './hooks';
import { PreviewCardContainer } from './components/PreviewCardContainer';
import { ContentLengthProgress } from './components/ContentLengthProgress';
import { EditorEmojiPicker } from './components/EditorEmojiPicker';
import { ImageUpload } from './components/ImageUpload';
import { SIZE_LIMIT } from './const';
import { ChangeData, RichTextData, Channel } from './types';
import {
  getImpreciseTextContentFromNode,
  parserRichTextContent,
  advancedProcessRichTextContent,
  getUrlsFromTextContent,
  AUTO_LINK_PLUGIN_MATCHER,
  getTextUnicodeLength,
  insertTextToEditor,
  initProposalTemplate,
  getCurrentEditorContent,
  initPost,
} from './utils';
import styles from './index.module.scss';
import { OriginalSwitch } from '../PostFooter/components/OriginalSwitch';

export type RichTextEditorHandle = {
  getSubmitContent: () => Promise<RichTextData>;
};

type RichTextEditorProps = {
  onContentChange?: (data: ChangeData) => void;
  initialValue?: string;
  onConfirm?: (data: EditorSubmitContentData) => Promise<void>;
  maxLength?: number;
  placeholder?: ReactNode;
  onPostSuccess?: () => Promise<void>;
  editorClassName?: string;
  placeholderClassName?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  disableCard?: boolean;
  isDarkMode?: boolean;
  disableUploadImage?: boolean;
  headerSlot?: ReactNode;
  toolbarSlot?: ReactNode;
  wrapperClassName?: string;
  // TODO：应单独抽取 TextEditor
  toolbar?: boolean;
  footer?: boolean;
  postInToolbar?: boolean;
  toolbarPostBtnClassName?: string;
  proposalTemplate?: UnionAccount;
  // 必须有 文本/图片 才为 valid
  onInvalidChange?(isInvalid: boolean): void;
  onImageUploading?(isUploading: boolean): void;
  defaultContent?: string;
  language?: SUPPORTED_LOCALE_TYPE;
  toolbarClassName?: string;
  onContentLengthChange?: (length: number) => void;
  initialPostData?: InitialPostData;
  cardClassName?: string;
  hiddenEligibilityFields?: string[];
  channel?: Channel;
  hideChannelPicker?: boolean;
  bodySlot?: ReactNode;
  required?: boolean;
  onImageChange?: (name?: string) => void;
};

const RichTextEditorInner = forwardRef<RichTextEditorHandle, RichTextEditorProps>((props, ref) => {
  const {
    onContentChange,
    placeholder,
    editorClassName,
    maxLength = 2000,
    onConfirm,
    onPostSuccess,
    disableUploadImage = false,
    isDarkMode,
    placeholderClassName,
    toolbar = true,
    autoFocus = true,
    disabled,
    disableCard,
    headerSlot,
    toolbarSlot,
    footer = true,
    wrapperClassName,
    proposalTemplate,
    onInvalidChange,
    onImageUploading,
    defaultContent,
    language,
    postInToolbar,
    toolbarPostBtnClassName,
    toolbarClassName,
    onContentLengthChange,
    initialPostData,
    cardClassName,
    hiddenEligibilityFields,
    channel,
    hideChannelPicker,
    required = true,
    onImageChange,
    bodySlot,
  } = props;

  useSyncI18n(language);
  const { t } = useTranslation();
  const [editor] = useLexicalComposerContext();
  const [selectedFile, setSelectedFile] = useState<{
    file?: any;
    id?: string;
    previewUrl?: string;
  } | null>();
  const [lastUrl, setLastUrl] = useState<string>();
  const { mutateAsync: uploadImage } = useCommonImageUpload('feed');
  const { on: isSubmitting, turnOn: startSubmit, turnOff: stopSubmit } = useSwitch(false);
  const [contentLength, setContentLength] = useState(0);
  const [isEmptyContent, setIsEmptyContent] = useState(!!required);
  const cardPreviewKey = useRef(Math.random()).current;
  const { data: lastUrlPreview } = useUrlCardPreview(lastUrl, cardPreviewKey);
  const isUploadingRef = useRef(false);
  const { setTheme } = useTheme();
  const [commentEligibility, setCommentEligibility] = useCommentEligibility();
  const { on: isOriginal, turn } = useSwitch();
  const { channelId } = useChannelPick(
    initialPostData ? initialPostData?.channel || null : channel,
  );

  useEffect(() => {
    if (proposalTemplate) {
      initProposalTemplate(editor, proposalTemplate);
    }
  }, [editor, proposalTemplate]);

  useEffect(() => {
    if (initialPostData) {
      // 回填文本内容，url card会重新获取
      initPost(editor, initialPostData);
      // 回填图片
      const imgUrl = initialPostData?.images?.map(i => i?.raw?.full_url)?.[0];
      const regex = /\/([a-f0-9]+)\.[a-z]+$/i;
      const imgId = imgUrl?.match(regex)?.[1];
      if (imgId) {
        setSelectedFile({ id: imgId, previewUrl: imgUrl });
      }
      // 没有使用组件内footer的，比如thread，在外面回填
      if (footer) {
        // 回填权限
        if (initialPostData?.permissions) {
          setCommentEligibility(initialPostData?.permissions);
        }
        // 回填isOriginal
        if (initialPostData?.is_original) {
          turn(true);
        }
      }
    }
  }, [editor, footer, initialPostData, setCommentEligibility, turn]);

  useEffect(() => {
    if (editor && defaultContent) {
      insertTextToEditor(editor, defaultContent);
      setIsEmptyContent(false);
    }
  }, [editor, defaultContent]);

  useImperativeHandle(
    ref,
    () => {
      return {
        // 有副作用，会对当前输入内容排除空行，提交时用
        getSubmitContent: async () => {
          const content = await getCurrentEditorContent(editor);
          const result: RichTextData = { ...content };

          if (selectedFile?.id) {
            result.image_ids = [selectedFile?.id];
          } else if (lastUrlPreview?.id) {
            result.card_id = lastUrlPreview?.id;
          }

          return result;
        },
      };
    },
    [editor, selectedFile?.id, lastUrlPreview?.id],
  );

  // format
  // const [isBold, setIsBold] = useState(false);

  // const updateToolbarStatus = useCallback(() => {
  //   editor.getEditorState().read(() => {
  //     if (editor.isComposing()) {
  //       return;
  //     }
  //     const selection = $getSelection();
  //     const nativeSelection = window.getSelection();
  //     const rootElement = editor.getRootElement();

  //     if (
  //       nativeSelection !== null &&
  //       (!$isRangeSelection(selection) ||
  //         rootElement === null ||
  //         !rootElement.contains(nativeSelection.anchorNode))
  //     ) {
  //       // is not text
  //       return;
  //     }

  //     if (!$isRangeSelection(selection)) {
  //       return;
  //     }
  //     // Update text format
  //     setIsBold(selection.hasFormat('bold'));
  //   });
  // }, [editor]);

  // useEffect(() => {
  //   document.addEventListener('selectionchange', updateToolbarStatus);
  //   return () => {
  //     document.removeEventListener('selectionchange', updateToolbarStatus);
  //   };
  // }, [updateToolbarStatus]);

  useEffect(() => {
    setTheme(isDarkMode ? THEMES.DARK : THEMES.DEFAULT);
  }, [isDarkMode, setTheme]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);

      // if (!disabled && autoFocus) {
      //   editor.focus();
      // }
    }
  }, [disabled, editor]);

  const isInvalid = useMemo(
    () =>
      Boolean(
        // 既没有内容，又没有上传图片
        (isEmptyContent && !selectedFile?.id) ||
          (maxLength && contentLength > maxLength) ||
          disabled,
      ),
    [isEmptyContent, selectedFile?.id, maxLength, contentLength, disabled],
  );

  useEffect(() => {
    onInvalidChange?.(isInvalid);
  }, [isInvalid, onInvalidChange]);

  const parserContent = useCallback(() => {
    const editorState = editor.getEditorState();
    editorState.read(() => {
      const root = $getRoot();
      const contentStr = getImpreciseTextContentFromNode(root);
      const urls = getUrlsFromTextContent(contentStr);
      if (required) {
        setIsEmptyContent(contentStr.trim().length === 0);
      }
      if (!disableCard) {
        setLastUrl(urls[urls.length - 1]);
      }
      setContentLength(getTextUnicodeLength(contentStr));
      onContentLengthChange?.(getTextUnicodeLength(contentStr));
      devLog('parserContent', {
        root,
        contentStr,
        urls,
        length: contentStr.length,
      });
    });
  }, [editor, disableCard, onContentLengthChange, required]);

  const debouncedParserContent = useMemo(() => debounce(parserContent, 500), [parserContent]);

  const onChange = useCallback(
    editorState => {
      editorState.read(() => {
        // Read the contents of the EditorState here.
        debouncedParserContent();

        if (onContentChange) {
          const { textContent, mentionsList, hashtagsList, urlsWidthIndices, slashesList } =
            parserRichTextContent(editor);
          onContentChange({
            textContent,
            mentionsList,
            hashtagsList,
            urlsWidthIndices,
            slashesList,
          });
        }
      });
    },
    [debouncedParserContent, editor, onContentChange],
  );

  const clearImage = useCallback(() => {
    setSelectedFile(undefined);
    isUploadingRef.current = false;
    onImageChange?.(undefined);
  }, [onImageChange]);

  const clearUrlPreview = useCallback(() => {
    lastUrl && clearUrlCardPreviewQuery(lastUrl, cardPreviewKey);
  }, [lastUrl, cardPreviewKey]);

  const handleBeforeUpload = useCallback(
    (file: RcFile) => {
      onImageUploading?.(true);
      return new Promise<void>(async (resolve, reject) => {
        if (file.size > SIZE_LIMIT) {
          message.error('image should be less than 5MB');
          return reject();
        }
        setSelectedFile({ file });
        onImageChange?.(file.name);
        isUploadingRef.current = true;
        const imageRes: any = await uploadImage(file).catch(e => {
          message.warn(e);
          clearImage();
        });
        if (imageRes?.image?.id && isUploadingRef.current) {
          setSelectedFile({ file, id: imageRes?.image?.id });
        }
        isUploadingRef.current = false;
        onImageUploading?.(false);
      });
    },
    [clearImage, uploadImage, onImageUploading, onImageChange],
  );

  const selectedFileUrl = useMemo(
    () =>
      selectedFile?.file
        ? window.URL.createObjectURL(selectedFile?.file)
        : selectedFile?.previewUrl || '',
    [selectedFile],
  );

  const clearAllContent = useCallback(() => {
    editor.update(() => {
      $getRoot().clear();
      // setIsBold(false);
    });
    setLastUrl(undefined);
    clearImage();
  }, [clearImage, editor]);

  const handleSubmitClick = useCallback(async () => {
    if (isSubmitting) {
      return;
    }
    let imageId = '';
    startSubmit();
    // 如果是编辑的情况，有可能只有id
    if (selectedFile?.file || selectedFile?.id) {
      if (!selectedFile?.id) {
        console.log('uploading image');
        stopSubmit();
        return;
      }
      imageId = selectedFile?.id;
    }
    const editorState = editor.getEditorState();

    editorState.read(async () => {
      const {
        // boldRangeList,
        mentionsList,
        hashtagsList,
        textContent,
        urlsWidthIndices,
        slashesList,
      } = await advancedProcessRichTextContent(editor);

      if (onConfirm) {
        const data: EditorSubmitContentData = {
          content: textContent,
          entities: {
            // richtext: [],
            // boldRangeList.map(range => ({
            //   indices: range,
            //   type: 'Bold',
            // })),
            ...(mentionsList.length ? { mentions: mentionsList } : {}),
            ...(hashtagsList.length ? { tags: hashtagsList } : {}),
            ...(urlsWidthIndices.length ? { url: urlsWidthIndices } : {}),
            ...(slashesList.length ? { channels: slashesList } : {}),
          },
          permissions: commentEligibility,
          is_original: isOriginal,
        };
        if (imageId) {
          data.image_ids = [imageId];
        } else if (lastUrlPreview?.id) {
          data.card_id = lastUrlPreview?.id;
        }

        if (channelId) {
          data.channel_id = channelId;
        }

        console.log('submitContent', data);

        onConfirm(data)
          .then(() => {
            clearAllContent();
            onPostSuccess && onPostSuccess();
          })
          .finally(() => {
            stopSubmit();
          });
      }
    });
  }, [
    isSubmitting,
    startSubmit,
    selectedFile?.file,
    selectedFile?.id,
    editor,
    stopSubmit,
    onConfirm,
    commentEligibility,
    isOriginal,
    lastUrlPreview?.id,
    clearAllContent,
    onPostSuccess,
    channelId,
  ]);

  // const toggleBoldFormat = useCallback(() => {
  //   editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
  // }, [editor]);

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (e: ClipboardEvent) => {
        if (!e.clipboardData || disableUploadImage) return false;
        const items = e.clipboardData.items;
        if (items && items.length) {
          for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1) {
              const file = items[i].getAsFile();
              if (file) {
                handleBeforeUpload(file as RcFile);
              }
              break;
            }
          }
        }
        return false;
      },
      4,
    );
  }, [disableUploadImage, editor, handleBeforeUpload]);

  const handleEmojiSelect = useCallback(
    (text: string) => {
      if (editor) {
        insertTextToEditor(editor, text);
      }
    },
    [editor],
  );

  return (
    <>
      <div className={cx(styles.editorWrapper, wrapperClassName)}>
        {headerSlot ? headerSlot : null}

        <SlashPlugin />
        <HashtagPlugin />
        <MentionsPlugin />
        <RichTextPlugin
          contentEditable={
            <ContentEditable className={cx(styles.editor, editorClassName)} autoFocus={autoFocus} />
          }
          placeholder={
            <span className={cx(styles.placeholder, placeholderClassName)}>
              {placeholder || t('Qa_Answer.holderCommon')}
            </span>
          }
          ErrorBoundary={LexicalErrorBoundary}
        />

        <OnChangePlugin onChange={onChange} />
        <ClearEditorPlugin />
        <HistoryPlugin />
        <AutoLinkPlugin matchers={AUTO_LINK_PLUGIN_MATCHER} />
        {selectedFileUrl || lastUrlPreview ? (
          <div className={styles.card}>
            {selectedFileUrl ? (
              <div className={cx(styles.previewImageWrapper, cardClassName)}>
                <IconRcCloseIcon
                  className={styles.previewCloseIcon}
                  onClick={clearImage}
                  width={32}
                  height={32}
                />
                <img className={styles.previewImage} src={selectedFileUrl} alt="" />
                {selectedFile?.id ? null : <Loading className={styles.imageLoading} />}
              </div>
            ) : lastUrlPreview ? (
              <PreviewCardContainer
                previewCard={lastUrlPreview}
                className={cx(styles.previewCardWrapper, cardClassName)}>
                <IconRcCloseIcon
                  className={styles.previewCloseIcon}
                  onClick={clearUrlPreview}
                  width={32}
                  height={32}
                />
              </PreviewCardContainer>
            ) : null}
          </div>
        ) : null}
        {bodySlot}
        {toolbar ? (
          <div className={cx(styles.toolbar, toolbarClassName)}>
            {toolbarSlot}
            {toolbarSlot ? <div className={styles.divider} /> : null}
            <div className={styles.toolbarOperateButtons}>
              {disableUploadImage ? null : (
                <ImageUpload
                  className={styles.toolbarOperateButton}
                  beforeUpload={handleBeforeUpload}
                />
              )}
              {/* <Button
            className={cx(styles.toolbarOperateButton, isBold && styles.isBoldActive)}
            type="text"
            onClick={toggleBoldFormat}>
            {isBold ? <IconRcBoldFormatActiveIcon /> : <IconRcBoldFormatIcon />}
          </Button> */}
              <EditorEmojiPicker onEmojiSelect={handleEmojiSelect} />
            </div>

            <div className={styles.toolbarRight}>
              <ContentLengthProgress currentLength={contentLength} maxLimit={maxLength} />
              {postInToolbar ? (
                <Button
                  type="primary"
                  className={cx(styles.submitButton, toolbarPostBtnClassName)}
                  loading={isSubmitting}
                  disabled={isInvalid}
                  onClick={handleSubmitClick}>
                  {t('PostFooter.1hg3k')}
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
      {footer ? (
        <PostFooter
          disabled={isInvalid}
          isLoading={isSubmitting}
          onSubmit={handleSubmitClick}
          hiddenEligibilityFields={hiddenEligibilityFields}
          channelPickerDisabled={!!initialPostData}
          key={initialPostData?.channel?.id}
          slot={
            <OriginalSwitch
              isOriginal={isOriginal}
              onChange={turn}
              disabled={!(contentLength && contentLength >= 120)}
            />
          }
          hideChannelPicker={hideChannelPicker}
        />
      ) : null}
    </>
  );
});

export const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  (props, ref) => {
    const onError = useCallback(error => {
      console.warn('richtext editor', error);
    }, []);

    const initialEditorConfig = useMemo(
      () => ({
        namespace: 'RichTextEditor',
        theme: {},
        nodes: [AutoLinkNode, MentionNode, HashtagNode, SlashNode],
        onError,
      }),
      [onError],
    );

    return (
      <QueryClientProvider client={queryClient}>
        <LexicalComposer initialConfig={initialEditorConfig}>
          <RichTextEditorInner {...props} ref={ref} />
        </LexicalComposer>
      </QueryClientProvider>
    );
  },
);

export * from './utils/utils';
export * from './types';
